<script setup lang="ts">
/**
 * DocSearch — zoeken in de documentatie.
 *
 * Zit in de header en staat dus op elke pagina. Opent met een klik of met
 * ⌘K / Ctrl+K, want dat is wat iedereen in documentatie inmiddels probeert.
 *
 * De afscherming zit aan de serverkant (zie SearchController): categorieën
 * waar deze bezoeker niet in mag leveren geen treffers, dus hier hoeft niets
 * gefilterd te worden.
 */

import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { Search, Loader2, CornerDownLeft } from 'lucide-vue-next'
import api from '../../api/client'

interface SearchResult {
  path: string
  title: string
  section: string
  snippet: string
}

const { t } = useI18n()
const router = useRouter()

const open = ref(false)
const query = ref('')
const results = ref<SearchResult[]>([])
const isSearching = ref(false)
const activeIndex = ref(0)
const inputEl = ref<HTMLInputElement | null>(null)
const listEl = ref<HTMLElement | null>(null)

let debounceTimer: ReturnType<typeof setTimeout> | undefined
/** Volgnummer per verzoek: een traag antwoord mag een sneller niet overschrijven. */
let requestId = 0

const hasQuery = computed(() => query.value.trim().length >= 2)
const showEmpty = computed(() => hasQuery.value && !isSearching.value && results.value.length === 0)

function openSearch() {
  open.value = true
  nextTick(() => inputEl.value?.focus())
}

function closeSearch() {
  open.value = false
  query.value = ''
  results.value = []
  activeIndex.value = 0
}

function onGlobalKey(event: KeyboardEvent) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    open.value ? closeSearch() : openSearch()
  }
}

onMounted(() => window.addEventListener('keydown', onGlobalKey))
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKey)
  clearTimeout(debounceTimer)
})

watch(query, () => {
  clearTimeout(debounceTimer)
  activeIndex.value = 0

  if (!hasQuery.value) {
    results.value = []
    isSearching.value = false
    return
  }

  isSearching.value = true
  // Wachten tot iemand even ophoudt met typen: anders gaat er per
  // toetsaanslag een zoekopdracht over de hele inhoud heen.
  debounceTimer = setTimeout(runSearch, 250)
})

async function runSearch() {
  const term = query.value.trim()
  const id = ++requestId

  try {
    const response = await api.get('/search', { params: { q: term } })

    if (id !== requestId) return

    results.value = response.data.results ?? []
  } catch {
    if (id !== requestId) return

    // Stil falen is hier juist: een mislukte zoekopdracht is geen fout die
    // het lezen in de weg mag zitten. De lege staat vertelt genoeg.
    results.value = []
  } finally {
    if (id === requestId) isSearching.value = false
  }
}

function move(delta: number) {
  if (results.value.length === 0) return

  const next = activeIndex.value + delta
  activeIndex.value = (next + results.value.length) % results.value.length

  nextTick(() => {
    listEl.value
      ?.querySelector(`[data-index="${activeIndex.value}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  })
}

function openResult(result: SearchResult | undefined) {
  if (!result) return

  router.push(`/docs/${result.path}`)
  closeSearch()
}

/**
 * Het fragment opgeknipt rond de zoekterm, zodat de treffer gemarkeerd kan
 * worden zonder HTML uit de server te vertrouwen.
 */
function parts(snippet: string): { text: string; match: boolean }[] {
  const term = query.value.trim()

  if (term === '') return [{ text: snippet, match: false }]

  const pieces: { text: string; match: boolean }[] = []
  const lowerSnippet = snippet.toLowerCase()
  const lowerTerm = term.toLowerCase()
  let cursor = 0

  for (;;) {
    const hit = lowerSnippet.indexOf(lowerTerm, cursor)
    if (hit === -1) break

    if (hit > cursor) pieces.push({ text: snippet.slice(cursor, hit), match: false })
    pieces.push({ text: snippet.slice(hit, hit + term.length), match: true })
    cursor = hit + term.length
  }

  if (cursor < snippet.length) pieces.push({ text: snippet.slice(cursor), match: false })

  return pieces
}

defineExpose({ openSearch })
</script>

<template>
  <button
    type="button"
    class="flex-shrink-0 inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
    @click="openSearch"
  >
    <Search class="w-4 h-4" aria-hidden="true" />
    <span class="hidden sm:inline">{{ t('search.open') }}</span>
    <kbd class="hidden lg:inline rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-sans text-gray-400">
      {{ t('search.shortcut') }}
    </kbd>
    <span class="sr-only sm:hidden">{{ t('search.open') }}</span>
  </button>

  <TransitionRoot :show="open" as="template">
    <Dialog class="relative z-50" @close="closeSearch">
      <TransitionChild
        as="template"
        enter="duration-200 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-150 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/40" aria-hidden="true"></div>
      </TransitionChild>

      <div class="fixed inset-0 flex items-start justify-center p-4 pt-[10vh]">
        <TransitionChild
          as="template"
          enter="duration-200 ease-out"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="duration-150 ease-in"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel class="w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl">
            <div class="flex items-center gap-3 border-b border-gray-200 px-4">
              <Search class="w-5 h-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
              <input
                ref="inputEl"
                v-model="query"
                type="text"
                role="combobox"
                aria-expanded="true"
                aria-controls="docsearch-results"
                :aria-activedescendant="results.length ? `docsearch-result-${activeIndex}` : undefined"
                :placeholder="t('search.placeholder')"
                :aria-label="t('search.label')"
                class="w-full border-0 py-4 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
                @keydown.down.prevent="move(1)"
                @keydown.up.prevent="move(-1)"
                @keydown.enter.prevent="openResult(results[activeIndex])"
              />
              <Loader2
                v-if="isSearching"
                class="w-4 h-4 flex-shrink-0 animate-spin text-gray-400"
                aria-hidden="true"
              />
            </div>

            <!-- Resultaten -->
            <ul
              v-if="results.length"
              id="docsearch-results"
              ref="listEl"
              role="listbox"
              :aria-label="t('search.resultsLabel')"
              class="max-h-[60vh] overflow-y-auto py-2"
            >
              <li
                v-for="(result, index) in results"
                :id="`docsearch-result-${index}`"
                :key="result.path"
                :data-index="index"
                role="option"
                :aria-selected="index === activeIndex"
                class="cursor-pointer px-4 py-2.5"
                :class="index === activeIndex ? 'bg-primary-50' : ''"
                @mouseenter="activeIndex = index"
                @click="openResult(result)"
              >
                <div class="flex items-baseline justify-between gap-3">
                  <span class="text-sm font-medium text-gray-900">{{ result.title }}</span>
                  <span v-if="result.section" class="flex-shrink-0 text-xs text-gray-400">
                    {{ result.section }}
                  </span>
                </div>
                <p v-if="result.snippet" class="mt-0.5 line-clamp-2 text-xs text-gray-500">
                  <template v-for="(part, i) in parts(result.snippet)" :key="i">
                    <mark v-if="part.match" class="bg-primary-100 text-primary-900">{{ part.text }}</mark>
                    <template v-else>{{ part.text }}</template>
                  </template>
                </p>
              </li>
            </ul>

            <!-- Geen treffers -->
            <p v-else-if="showEmpty" class="px-4 py-8 text-center text-sm text-gray-500">
              {{ t('search.noResults', { query: query.trim() }) }}
            </p>

            <!-- Nog niets getypt -->
            <p v-else-if="!hasQuery" class="px-4 py-8 text-center text-sm text-gray-400">
              {{ t('search.hint') }}
            </p>

            <div
              v-if="results.length"
              class="flex items-center gap-4 border-t border-gray-100 px-4 py-2 text-[11px] text-gray-400"
            >
              <span class="inline-flex items-center gap-1">
                <CornerDownLeft class="w-3 h-3" aria-hidden="true" /> {{ t('search.toOpen') }}
              </span>
              <span>{{ t('search.toNavigate') }}</span>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
